import { HISTORY_ACTION_MODE } from '@candulabs/craft';
import { styled } from 'canoe';
import { nanoid } from 'nanoid';
import React, { ReactElement, useEffect } from 'react';

import { toCssSelector } from '@candu-ai/stylesheet/utils';
import { CloseButton } from 'craft/components/CloseButton';
import Node from 'craft/components/Node';
import Placeholder from 'craft/components/Placeholder';
import { createVariantsSelector } from 'craft/components/SelectedToolbar/VariantsSelector';
import { VariantsSelectorToolbox } from 'craft/components/VariantsSelectorToolbox';
import { useEditor, useNodeContext } from 'craft/hooks';
import { CustomNodeData, ToolboxComponentSections } from 'craft/types';
import { hasDescendants } from 'craft/utils';

import { CardDismiss } from './toolbox/CardDismiss';

type Props<T extends React.ElementType> = {
  children?: ReactElement | ReactElement[];
  is?: T;
  placeholder?: ReactElement;
  custom?: CustomNodeData;
  id?: any;
  isDismissible?: boolean;
  variant?: string;
};

export const StyledCard = styled.div`
  margin: 0;
  position: relative;
`;

export const Card = <T extends React.ElementType>({
  children,
  placeholder,
  isDismissible,
  variant,
}: Props<T>) => {
  const { id } = useNodeContext();
  const { actions, query } = useEditor();
  const hasChildren = hasDescendants(children);

  const placeholderComponent = placeholder || <Placeholder />;

  const sssClassname = toCssSelector('card', {
    output: 'html',
    variant: variant ? { variant } : undefined,
  });

  // Add Card State only when isDismissible is true.
  // If we add this CardState immediately regardless of dismissible, all of our clients who are using the older SDK will fail
  // Because SDK will throw if it cannot find a matching state resolver
  useEffect(() => {
    const { states } = query.node(id).get().data.custom;

    if (isDismissible === undefined && states === undefined) {
      return;
    }
    if (!isDismissible) {
      actions.setCustom(
        id,
        (custom: CustomNodeData) => {
          // eslint-disable-next-line
          delete custom.states;
        },
        HISTORY_ACTION_MODE.IGNORE,
      );
      return;
    }

    // add a state provider if we do not have one
    if (Object.keys(states ?? {}).length === 0) {
      const stateId = nanoid(6);
      actions.setCustom(
        id,
        (custom: CustomNodeData) => {
          // eslint-disable-next-line
          custom.states = { [stateId]: {} };
        },
        HISTORY_ACTION_MODE.IGNORE,
      );
    }
  }, [isDismissible]);

  const variantClassName = `variant-base ${sssClassname}`;
  return (
    <Node is={StyledCard} size="lg" id={`cu-${id}`} className={variantClassName}>
      {isDismissible && <CloseButton />}
      {hasChildren ? children : placeholderComponent}
    </Node>
  );
};

Card.craft = {
  name: 'Card',
  isCanvas: true,
};

Card.toolbox = {
  component: [CardDismiss],
  schema: {
    [ToolboxComponentSections.NameAndType]: {
      variantSelector: VariantsSelectorToolbox,
    },
    [ToolboxComponentSections.Size]: {
      hasOverflow: true,
    },
  },
};

Card.selectedToolbar = {
  customItems: [
    createVariantsSelector({
      componentName: 'card',
      getConditions: (props) =>
        (props.variant?.value
          ? { name: props.variant?.value, variant: props.variant?.value }
          : { name: 'base' }) as Record<string, string>,
      variantToProps: (variant) => ({
        variant: variant.conditions.variant,
      }),
    }),
  ],
};
